import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { AuthUser } from '@authsemply/shared';
import { AuthCard } from '../components/AuthCard';
import { Field } from '../components/Field';
import { Alert } from '../components/Alert';
import { api, resumeAuthorizeUrl } from '../lib/api';
import { postLoginDestination } from '../lib/products';
import { useAuthStore } from '../store/auth.store';

export function MfaPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = resumeAuthorizeUrl(searchParams);
  const mfaToken = useAuthStore((state) => state.mfaToken);
  const setMfaToken = useAuthStore((state) => state.setMfaToken);
  const setUser = useAuthStore((state) => state.setUser);
  const [error, setError] = useState<string | null>(null);
  const [useRecovery, setUseRecovery] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [pending, setPending] = useState(false);
  const [recuperationDefi, setRecuperationDefi] = useState(true);

  /**
   * Récupère le défi posé par un retour de fournisseur externe (Google,
   * Microsoft, Apple).
   *
   * Après une connexion par mot de passe, le jeton est déjà en mémoire :
   * `/auth/login` vient de le rendre. Après un retour de fournisseur, la page a été
   * rechargée par une redirection et cette mémoire est vide — le défi attend
   * dans un cookie court, que l'API relaie ici. Sans ce chemin, un compte
   * protégé par un second facteur ne pourrait plus se connecter avec Google du
   * tout ; c'est ce qui rend la correction du contournement acceptable côté
   * usage, et non une simple fermeture de porte.
   */
  useEffect(() => {
    if (mfaToken) {
      setRecuperationDefi(false);
      return;
    }
    let vivant = true;
    api
      .get<{ mfaToken: string | null }>('/oauth/mfa')
      .then((reponse) => {
        if (!vivant) return;
        if (reponse.mfaToken) setMfaToken(reponse.mfaToken);
        setRecuperationDefi(false);
      })
      .catch(() => {
        if (vivant) setRecuperationDefi(false);
      });
    return () => {
      vivant = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!mfaToken) {
      // Pas de défi : soit la page a été ouverte directement, soit le cookie
      // de cinq minutes a expiré. Dans les deux cas, on recommence.
      navigate('/login');
      return;
    }

    setPending(true);
    setError(null);
    const form = new FormData(event.currentTarget);

    try {
      const result = await api.post<{ user: AuthUser }>('/auth/login/mfa', {
        mfaToken,
        code: useRecovery ? '' : form.get('code'),
        recoveryCode: useRecovery ? form.get('code') : undefined,
      });
      setUser(result.user);
      if (returnTo) {
        window.location.assign(returnTo);
        return;
      }
      const destination = await postLoginDestination(searchParams);
      if ('external' in destination) window.location.assign(destination.external);
      else navigate(destination.internal);
    } catch {
      setError('Code invalide ou expiré.');
    } finally {
      setPending(false);
    }
  }

  // Le défi arrive peut-être encore du cookie posé par le retour du fournisseur :
  // afficher le formulaire maintenant le ferait rebondir vers /login au premier
  // envoi, pour une raison invisible à l'utilisateur.
  if (recuperationDefi) {
    return (
      <AuthCard title="Validation en deux étapes" subtitle="Un instant…">
        <p />
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Validation en deux étapes"
      subtitle={
        useRecovery
          ? 'Saisissez un code de secours.'
          : 'Saisissez le code à 6 chiffres de votre application d’authentification.'
      }
    >
      <Alert message={error} />
      <form onSubmit={handleSubmit}>
        <Field
          label={useRecovery ? 'Code de secours' : 'Code à 6 chiffres'}
          name="code"
          inputMode={useRecovery ? 'text' : 'numeric'}
          autoComplete="one-time-code"
          required
        />
        <button className="btn" type="submit" disabled={pending}>
          {pending ? 'Vérification…' : 'Valider'}
        </button>
      </form>
      <div className="links" style={{ flexDirection: 'column', gap: '0.5rem' }}>
        <button
          type="button"
          className="btn btn-secondary"
          style={{ marginTop: '1rem', width: '100%' }}
          onClick={() => setUseRecovery((value) => !value)}
        >
          {useRecovery ? 'Utiliser mon application' : 'Utiliser un code de secours'}
        </button>
        {/* Dernier recours : un e-mail de désactivation. Possible uniquement
            parce que le mfaToken atteste qu'un premier facteur a été fourni
            (mot de passe, ou Google / Microsoft / Apple). */}
        {resetSent ? (
          <p className="subtitle" style={{ margin: 0 }}>
            E-mail envoyé : suivez le lien pour désactiver la double authentification.
          </p>
        ) : (
          <button
            type="button"
            className="btn-link"
            onClick={() => {
              if (!mfaToken) return;
              setResetSent(true);
              void api.post('/auth/mfa/recovery', { mfaToken }).catch(() => undefined);
            }}
          >
            J'ai perdu l'accès à mon application et à mes codes
          </button>
        )}
      </div>
    </AuthCard>
  );
}
